import { positionPopover, unfoldPopover } from './popover.js?v=20261104-motion';

if (!customElements.get('meri-localization')) {
  customElements.define(
    'meri-localization',
    class extends HTMLElement {
      connectedCallback() {
        this.events?.abort();
        this.observer?.disconnect();
        this.events = new AbortController();
        const initialize = () => {
          const form = this.querySelector('form');
          const submit = form?.querySelector('[type="submit"]');
          if (!submit) return false;
          const selects = [...form.querySelectorAll('select[name="country_code"], select[name="language_code"]')];
          if (selects.some((select) => !select.selectedOptions.length)) return false;
          this.observer?.disconnect();
          this.querySelectorAll('[data-localization-ui]').forEach((node) => node.remove());
          const options = { signal: this.events.signal };
          const enhanced = selects.filter(
            (select) => 'showPopover' in HTMLElement.prototype && select.closest('.localization__field'),
          );
          // A native select fires change on every arrow key in some browsers, so it keeps the
          // Apply button (WCAG 3.2.2). Only the enhanced picker, chosen explicitly, submits at once.
          submit.hidden = enhanced.length === selects.length;
          selects.forEach((select) => {
            select.hidden = false;
            if (select.closest('.localization__field')) select.closest('.localization__field').hidden = false;
            if (enhanced.includes(select)) this.enhance(select, options);
          });
          form.addEventListener(
            'change',
            (event) => {
              if (enhanced.includes(event.target)) form.requestSubmit();
            },
            options,
          );
          return true;
        };
        if (!initialize()) {
          this.observer = new MutationObserver(initialize);
          this.observer.observe(this, { childList: true, subtree: true });
        }
      }

      enhance(select, events) {
        const field = select.closest('.localization__field');
        const label = field.querySelector('.visually-hidden').textContent.trim();
        const create = (tag, className, text) => {
          const node = document.createElement(tag);
          node.className = className;
          if (text) node.textContent = text;
          return node;
        };
        const wrapper = create('div', 'localization__control');
        wrapper.dataset.localizationUi = '';
        wrapper.dataset.localizationName = select.name;
        const trigger = create('button', 'localization__trigger');
        trigger.type = 'button';
        const flag = (option) => {
          if (!option.dataset.flag) return null;
          const image = create('img', 'localization__flag');
          image.src = option.dataset.flag;
          image.alt = '';
          image.width = 20;
          image.height = 15;
          image.loading = 'lazy';
          return image;
        };
        const value = create('span', 'localization__value');
        const updateValue = (option) => {
          trigger.querySelector('.localization__flag')?.remove();
          const image = flag(option);
          if (image) {
            image.loading = 'eager';
            trigger.prepend(image);
          }
          value.textContent = option.dataset.compact || option.label;
          const name = option.label.includes(value.textContent)
            ? option.label
            : value.textContent + ' — ' + option.label;
          trigger.setAttribute('aria-label', label + ': ' + name);
        };
        trigger.append(value);
        updateValue(select.selectedOptions[0]);
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-expanded', 'false');
        const panel = create('div', 'localization__popover');
        panel.id = select.id + '-popover';
        panel.popover = 'auto';
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-label', label);
        trigger.setAttribute('aria-controls', panel.id);
        trigger.setAttribute('popovertarget', panel.id);
        const header = create('div', 'localization__header');
        const title = create('span', 'localization__title', label);
        const close = create('button', 'localization__close', '×');
        close.type = 'button';
        close.setAttribute('aria-label', this.dataset.close);
        header.append(title, close);
        panel.append(header);
        let search;
        if (select.name === 'country_code') {
          search = create('input', 'localization__search');
          search.type = 'search';
          search.autocomplete = 'off';
          search.placeholder = this.dataset.search;
          search.setAttribute('aria-label', this.dataset.search + ': ' + label);
          panel.append(search);
        }
        const list = create('div', 'localization__list');
        list.setAttribute('role', 'group');
        list.setAttribute('aria-label', label);
        const buttons = [...select.options].map((option) => {
          const button = create('button', 'localization__option', option.label);
          const image = flag(option);
          if (image) button.prepend(image);
          button.type = 'button';
          button.setAttribute('aria-pressed', String(option.selected));
          if (option.lang) button.lang = option.lang;
          button.addEventListener(
            'click',
            () => {
              panel.hidePopover();
              trigger.focus({ preventScroll: true });
              if (select.value === option.value) return;
              select.value = option.value;
              updateValue(option);
              buttons.forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
              select.dispatchEvent(new Event('change', { bubbles: true }));
            },
            events,
          );
          list.append(button);
          return button;
        });
        const empty = create('p', 'localization__empty', this.dataset.empty);
        empty.setAttribute('role', 'status');
        empty.hidden = true;
        panel.append(list, empty);
        wrapper.append(trigger, panel);
        field.after(wrapper);
        select.hidden = true;
        field.hidden = true;
        const position = () => {
          const mobile = matchMedia('(max-width: 760px)').matches;
          const country = select.name === 'country_code';
          positionPopover(
            panel,
            trigger,
            mobile ? (country ? 280 : 240) : country ? 340 : 260,
            mobile ? 360 : Infinity,
          );
        };
        panel.addEventListener(
          'beforetoggle',
          (event) => {
            const opening = event.newState === 'open';
            trigger.setAttribute('aria-expanded', String(opening));
            if (opening) {
              buttons.forEach((button) => (button.hidden = false));
              empty.hidden = true;
              if (search) search.value = '';
            }
          },
          events,
        );
        trigger.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            if (panel.matches(':popover-open')) {
              panel.hidePopover();
              return;
            }
            panel.showPopover();
            position();
            // Only opening animates; repositioning on scroll or a search keeps the list still.
            unfoldPopover(panel, buttons);
            (search || buttons[select.selectedIndex] || close).focus({ preventScroll: true });
          },
          events,
        );
        close.addEventListener(
          'click',
          () => {
            panel.hidePopover();
            trigger.focus({ preventScroll: true });
          },
          events,
        );
        search?.addEventListener(
          'input',
          () => {
            const term = search.value.trim().normalize('NFKD').toLocaleLowerCase();
            empty.textContent = this.dataset.empty.replace('[query]', search.value.trim());
            buttons.forEach(
              (button) => (button.hidden = !button.textContent.normalize('NFKD').toLocaleLowerCase().includes(term)),
            );
            empty.hidden = buttons.some((button) => !button.hidden);
            position();
          },
          events,
        );
        panel.addEventListener(
          'keydown',
          (event) => {
            const visible = buttons.filter((button) => !button.hidden);
            if (event.key === 'Escape') {
              event.preventDefault();
              panel.hidePopover();
              trigger.focus({ preventScroll: true });
            } else if (
              ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key) &&
              (event.target !== search || ['ArrowDown', 'ArrowUp'].includes(event.key))
            ) {
              event.preventDefault();
              const index = visible.indexOf(document.activeElement);
              const next =
                event.key === 'Home'
                  ? 0
                  : event.key === 'End'
                    ? visible.length - 1
                    : event.key === 'ArrowDown'
                      ? (index + 1) % visible.length
                      : (index <= 0 ? visible.length : index) - 1;
              visible[next]?.focus();
            } else if (event.key === 'Enter' && event.target === search) {
              event.preventDefault();
              visible[0]?.focus();
            }
          },
          events,
        );
        panel.addEventListener(
          'focusout',
          (event) => {
            if (event.relatedTarget && !wrapper.contains(event.relatedTarget) && panel.matches(':popover-open'))
              panel.hidePopover();
          },
          events,
        );
        const reposition = (event) => {
          // Scrolling the options does not move the anchor. Remeasuring here
          // temporarily expands the list and clamps its scrollTop in WebKit.
          if (event.target instanceof Node && panel.contains(event.target)) return;
          if (panel.matches(':popover-open')) position();
        };
        window.addEventListener('resize', reposition, events);
        window.addEventListener('scroll', reposition, { ...events, capture: true });
        window.visualViewport?.addEventListener('resize', reposition, events);
        window.visualViewport?.addEventListener('scroll', reposition, events);
      }

      disconnectedCallback() {
        this.events?.abort();
        this.observer?.disconnect();
        this.querySelectorAll('.localization__popover').forEach((panel) => {
          if (panel.matches(':popover-open')) panel.hidePopover();
        });
        this.querySelectorAll('.localization__field').forEach((field) => (field.hidden = false));
      }
    },
  );
}
